/**
 * MIRROR of `artifacts/api-server/src/lib/permissionsCatalog.ts`.
 * Keep this list in sync with the server-side catalog — the Settings UI uses
 * it to render the per-customer Read/Write checkbox matrix, and the dashboard
 * uses it to hide tabs the user can't read.
 */

export type TabId =
  | "masters"
  | "mission_control"
  | "forward_operating_base"
  | "main_operating_base"
  | "ai_command_control"
  | "mssp"
  | "triage"
  | "overview"
  | "incidents"
  | "simulation_lab"
  | "hunts"
  | "workbooks"
  | "notebook_ops"
  | "responseops"
  | "risk_command_center"
  | "advanced_hunting"
  | "phishing"
  | "darkweb"
  | "spiderfoot"
  | "dnstwist"
  | "coverage"
  | "oob_coverage_assessment"
  | "custom_analytics_studio"
  | "vulnerability_mgmt"
  | "entity_360"
  | "soc_digital_twin"
  | "soc_debt"
  | "report_builder"
  | "connector_health"
  | "detection_quality"
  | "customer_exec"
  | "datalake"
  | "reports"
  | "threat_intel"
  | "security_copilot"
  | "logs"
  | "log_master";

export interface TabDef {
  id: TabId;
  label: string;
  hint: string;
}

export const TAB_CATALOG: TabDef[] = [
  { id: "mission_control", label: "Mission Control", hint: "Central SOC command view with clickable KPIs, AI SOC agent status, customer risk wall, workflow pipeline, exports and drilldowns into KloudSecure modules." },
  { id: "forward_operating_base", label: "Forward Operating Base", hint: "Customer-scoped Forward Operating Base built from Sentinel, Defender, Entra, Syslog/CEF and Azure telemetry. Includes terrain topology, active-threat overlays, IoC/incident/alert/CVE relationships, risk indicators, graph deltas, immersive/pannable Stage 1C topology, historical snapshots, optional Sentinel Data Lake enrichment, source health and evidence lineage." },
  { id: "main_operating_base", label: "Main Operating Base", hint: "Stage 2D/2D.1/2D.2 live security architecture / digital twin derived from persisted FOB evidence and approved SOC Digital Twin critical assets. Includes expandable zones, bounded Intelligence Graph, entity focus, optional minimap, density controls, relationship evidence, semantic arcs, snapshot diff, risk heatmap, crown-jewel/AI C2 path illumination, historical architecture playback, attack evidence replay, AI C2 case-relative replay lane, snapshots and exports." },
  { id: "ai_command_control", label: "AI Command & Control Center", hint: "Customer-scoped AI C2 with Stage 2A context analysis, Stage 2B.3 progress-aware autonomous read-only investigation, and Stage 2C Investigation Workspace, Stage 3C Response Commander, and Stage 4 Independent Judge + guarded actions. Luna collects/compacts evidence, Terra leads correlation/RCA after routing checkpoints, and Sol performs justified expert review. Stage 2C provides a deterministic human case view, FOB threat overlay and Markdown/text export under Feature Flags, capability policy and audit. No containment/write tools are exposed." },
  { id: "mssp",         label: "MSSP Overview",   hint: "Cross-tenant rollup. Visible flag only - no per-customer read/write actions." },
  { id: "masters", label: "Masters", hint: "Developer-only module for role templates, permission policy, table access, report access, and platform governance.", },
  { id: "triage",       label: "Triage Queue",    hint: "Cross-tenant queue. Visible flag only - read/write are governed by Incidents on each tenant." },
  { id: "overview",     label: "Overview",        hint: "Per-customer overview KPIs. (No write actions today.)" },
  { id: "incidents",    label: "Incidents",       hint: "View incidents. Write: close, assign, comment, run AI investigations." },
  { id: "simulation_lab", label: "AI Simulation Lab", hint: "IncidentOps read-only AI investigation replay and training lab. Run simulations, replay evidence steps, compare verdicts and send weak labels into ML Insights." },
  { id: "hunts",        label: "HuntOps Command Center", hint: "Active threat hunting catalog, AI hunt planner, Microsoft Sentinel GitHub query sync, ASIM/parser readiness, findings inbox, bookmarks, lookback controls, schedules, manual hunt execution and saved hunts." },
  { id: "workbooks", label: "Workbooks", hint: "HuntOps workbook catalog, OOB/custom workbook governance, interactive Workbook Studio shell, import/export, AI generation planning, deployment placeholders and audit-ready versioning roadmap." },
  { id: "notebook_ops", label: "NotebookOps", hint: "Sentinel notebook catalog sync, safe preview, risk classification, trust governance, controlled Notebook Worker runs, read-only KQL execution, dependency preflight, polished run reports, Customer Master runtime context injection and Jupyter-backed Interactive Lab sessions." },
  { id: "responseops", label: "ResponseOps Playbook Studio", hint: "MSSP-safe playbook template library, Microsoft Sentinel GitHub playbook sync, AI design, safety scoring, approval gates, simulation, Logic App/Bicep artifact generation, selectable deployment target, disabled/manual-only deployment, runtime monitoring, adaptive playbook recommendations, step-by-step Logic App builder and exports." },
  { id: "risk_command_center", label: "Risk Command Center", hint: "MSSP leadership view that ranks customers by KloudSecure Risk Index using incidents, critical unresolved work, exposure, secure score, EDR, connector health, TI hits, SLA risk, risky entities and latest ingestion." },
  { id: "advanced_hunting", label: "Advanced Hunting", hint: "Run read-only KQL queries against selected customer Log Analytics workspaces." },
  { id: "phishing",     label: "Phishing",        hint: "View phishing telemetry. Write: trigger AI investigation, sender hunts, bypass analysis." },
  { id: "darkweb",      label: "External Exposure Center",   hint: "Correlate dark-web, credential, CT, code-leak, Shodan and curated lookalike-domain findings." },
  { id: "spiderfoot", label: "SpiderFoot", hint: "Run controlled OSINT scans through the integrated SpiderFoot service.", },
  { id: "dnstwist", label: "Phishing Domain Scanner", hint: "Run full dnstwist deep-combo typo-squat scans, view all permutations, and run AI confidence analysis.", },
  { id: "coverage",     label: "MITRE Coverage Command Center",  hint: "View ATT&CK, DEFEND/D3FEND, procedure-level analytic rule coverage, AI analysis and exports." },
  { id: "oob_coverage_assessment", label: "AnalyticsOps", hint: "Assess, map, deploy and drift-check Microsoft Sentinel out-of-the-box analytics content with manifest, catalog, table-based extraction and audited ARM workflows." },
  { id: "custom_analytics_studio", label: "Custom Analytics Studio", hint: "Generate, validate, score, version, drift-check, improve and deploy customer-specific Microsoft Sentinel analytic rules using single/multi-table profiling, reference detection URL conversion context, GPT suggestions, KQL/YAML/ARM previews, KloudSecure Repo, ARM diagnostics and full audit trail." },
  { id: "vulnerability_mgmt", label: "Vulnerability Management", hint: "View Microsoft Defender Vulnerability Management exposure, CVEs, recommendations, software risk, AI brief and PDF exports." },
  { id: "entity_360", label: "Entity 360", hint: "Universal entity profile for users, devices, IPs, domains, URLs, hashes, processes, mailboxes, cloud apps and CVEs." },
  { id: "soc_digital_twin", label: "SOC Digital Twin", hint: "Business-aware security decision engine that models assets, identities, detections, vulnerabilities, incidents, controls, playbooks and data sources." },
  { id: "soc_debt", label: "SOC Debt Dashboard", hint: "Governance dashboard for stale incidents, unowned work, noisy detections, connector/SOAR health, evidence retention and aged vulnerability debt." },
  { id: "report_builder", label: "AI Report Builder", hint: "Template and prompt-driven customer report generation with cached data and PDF/CSV/HTML exports." },
  { id: "connector_health", label: "Connector Health", hint: "Platform-level connector health and data freshness guardian for Sentinel, Defender, Logic Apps, ingestion, parsers and DCR/DCE status." },
  { id: "detection_quality", label: "Detection Quality Score", hint: "Detection engineering maturity score for analytics rules using alert volume, TP/FP rate, MITRE/entity coverage, SentinelHealth failures and SentinelAudit rule changes." },
  { id: "customer_exec", label: "Executive Portal", hint: "Read-only customer-facing executive security posture, trends, SLA, vulnerability backlog, business risk and report download view." },
  { id: "datalake",     label: "AI Assistant", hint: "View AI assistant + KQL console. Write: send chat / run KQL queries (uses paid APIs). Portal role is hard-blocked server-side." },
  { id: "reports",      label: "Reports",         hint: "View daily / weekly / monthly / quarterly / yearly reports. (No write actions today.)" },
  { id: "threat_intel", label: "Threat Intelligence", hint: "View Threat Landscape, Phase 3.5 Indicator Landscape and Phase 4 Customer Threat Relevance with explainable customer-specific actor scoring across targeting context, Sentinel incidents/alerts, ATT&CK overlap, customer IoCs and MDE exposure. Write: validate and submit reviewed actor-associated or analyst-provided IOCs to the selected customer." },
  { id: "security_copilot", label: "Security Copilot", hint: "Configure and use Microsoft Security Copilot promptbooks, agents and incident Ask Copilot workflows. Live mode can consume Security Compute Units." },
  { id: "logs",         label: "Operational Logs", hint: "View runtime log buffer. Admin-only feature; matrix entry exists for completeness." },
  { id: "log_master",   label: "Log Master",      hint: "View PostgreSQL audit/log/history tables, privileged audit events and CSV exports. Sensitive values are redacted server-side." },
];

export const ALL_TAB_IDS: TabId[] = TAB_CATALOG.map((t) => t.id);

/**
 * Tabs added AFTER the original 7-tab matrix shipped. Mirror of the server
 * `LEGACY_OPEN_TABS` set — see `api-server/src/lib/permissionsCatalog.ts`.
 * Missing cells for these tabs fall back to legacy default access so
 * pre-existing per-customer overrides don't accidentally hide them.
 */
export const LEGACY_OPEN_TABS: ReadonlySet<TabId> = new Set<TabId>([
  "mission_control",
  "forward_operating_base",
  "ai_command_control",
  "mssp",
  "triage",
  "overview",
  "notebook_ops",
  "workbooks",
  "simulation_lab",
  "oob_coverage_assessment",
  "custom_analytics_studio",
  "vulnerability_mgmt",
  "entity_360",
  "soc_digital_twin",
  "soc_debt",
  "report_builder",
  "connector_health",
  "detection_quality",
  "customer_exec",
  "datalake",
  "advanced_hunting",
  "spiderfoot",
  "dnstwist",
  "logs",
  "log_master",
]);

/**
 * Single source of truth for the customer-portal nav. Drives both:
 *  • the portal renderer (`CustomerPortal.tsx`) — for nav-bar order/labels
 *  • the per-customer matrix editor — for row filtering and presets
 *
 * Each entry maps a portal nav id (which the portal switches on) to the
 * catalog `TabId` used for permission lookups. The portal nav id and the
 * catalog id are deliberately allowed to differ (e.g. nav `ioc` →
 * permission `threat_intel`) so the portal URLs stay backwards
 * compatible while permission storage uses the canonical names.
 */
export interface PortalTabSpec {
  /** Portal nav id — switched on inside CustomerPortal. */
  id:
    | "overview"
    | "incidents"
    | "hunts"
    | "notebook_ops"
    | "responseops"
    | "risk_command_center"
    | "advanced_hunting"
    | "phishing"
    | "darkweb"
    | "spiderfoot"
    | "dnstwist"
    | "coverage"
    | "ioc"
    | "reports";
  label: string;
  /** Catalog tab id used for permission gating. */
  permission: TabId;
}

export const PORTAL_TABS_SPEC: readonly PortalTabSpec[] = [
  { id: "overview",  label: "Overview",                       permission: "overview" },
  { id: "incidents", label: "Incidents",                      permission: "incidents" },
  { id: "hunts",     label: "HuntOps",                        permission: "hunts" },
  { id: "advanced_hunting", label: "Advanced Hunting",            permission: "advanced_hunting" },
  { id: "phishing",  label: "Phishing Overview",              permission: "phishing" },
  { id: "darkweb",   label: "External Exposure Center",         permission: "darkweb" },
  { id: "spiderfoot", label: "SpiderFoot",                      permission: "spiderfoot" },
  { id: "dnstwist",   label: "Phishing Domain Scanner",          permission: "dnstwist" },
  { id: "coverage",  label: "MITRE",                         permission: "coverage" },
  { id: "ioc",       label: "Intelligence Overview", permission: "threat_intel" },
  { id: "reports",   label: "Reports",                        permission: "reports" },
];

/**
 * Set of catalog `TabId`s the portal can actually render. Derived from
 * `PORTAL_TABS_SPEC` (used by `CustomerPortal.tsx`) so the matrix editor
 * and the portal renderer can never drift apart.
 */
export const PORTAL_VISIBLE_TABS: ReadonlySet<TabId> = new Set<TabId>(
  PORTAL_TABS_SPEC.map((t) => t.permission),
);

/**
 * Per-cell shape — mirrored from the server. `visible` is optional in storage
 * for backwards compatibility (legacy cells without it are treated as
 * `visible = read || write`). Cascade: write ⇒ read ⇒ visible.
 */
export interface PermissionCell {
  visible?: boolean;
  read: boolean;
  write: boolean;
}

export type PermissionMatrix = Record<string, Record<string, PermissionCell>>;

/** True when the cell should appear in the user's nav. */
export function cellVisible(cell: PermissionCell): boolean {
  if (typeof cell.visible === "boolean") return cell.visible;
  return cell.read || cell.write;
}

/**
 * Maps the App.tsx nav tab id → the catalog TabId used for permission checks.
 * Every tab in the analyst nav now maps through the matrix. Role-based gates
 * (`adminOnly` / `hideForRoles`) in `App.tsx` still apply as defence in depth
 * — admin can never reveal `logs` to a non-admin via the matrix because the
 * `adminOnly` filter runs first.
 */
export const NAV_TAB_TO_PERMISSION: Record<string, TabId | undefined> = {
  masters: "masters",
  mission_control: "mission_control",
  forward_operating_base: "forward_operating_base",
  main_operating_base: "main_operating_base",
  ai_command_control: "ai_command_control",
  mssp: "mssp",
  triage: "triage",
  overview: "overview",
  incidents: "incidents",
  simulation_lab: "simulation_lab",
  hunts: "hunts",
  notebook_ops: "notebook_ops",
  workbooks: "workbooks",
  advanced_hunting: "advanced_hunting",
  phishing: "phishing",
  darkweb: "darkweb",
  spiderfoot: "spiderfoot",
  dnstwist: "dnstwist",
  coverage: "coverage",
  oob_coverage_assessment: "oob_coverage_assessment",
  custom_analytics_studio: "custom_analytics_studio",
  vulnerability_mgmt: "vulnerability_mgmt",
  entity_360: "entity_360",
  soc_digital_twin: "soc_digital_twin",
  soc_debt: "soc_debt",
  report_builder: "report_builder",
  connector_health: "connector_health",
  detection_quality: "detection_quality",
  customer_exec: "customer_exec",
  datalake: "datalake",
  reports: "reports",
  ioc: "threat_intel",
  security_copilot: "security_copilot",
  logs: "logs",
  log_master: "log_master",
};

/**
 * Maps the App.tsx / CustomerPortal.tsx nav tab id -> the Permissions Master
 * module + capability that gates its visibility. Shared by both navs so
 * Tab Visibility Master is the single source of truth for what shows in the
 * SOC dashboard sidebar AND the customer portal sidebar.
 */
export const TAB_CAPABILITY_MAP: Record<string, { moduleId: string; capability: string }> = {
  mission_control: { moduleId: 'mission_control', capability: 'visible' },
  forward_operating_base: { moduleId: 'forward_operating_base', capability: 'visible' },
  main_operating_base: { moduleId: 'main_operating_base', capability: 'visible' },
  ai_command_control: { moduleId: 'ai_command_control', capability: 'visible' },
  ml_insights: { moduleId: 'ml_insights', capability: 'visible' },
  simulation_lab: { moduleId: 'simulation_lab', capability: 'visible' },
  mssp: { moduleId: 'mssp', capability: 'visible' },
  overview: { moduleId: 'overview', capability: 'visible' },
  incidents: { moduleId: 'incidents', capability: 'visible' },
  hunts: { moduleId: 'hunts', capability: 'visible' },
  workbooks: { moduleId: 'workbooks', capability: 'visible' },
  notebook_ops: { moduleId: 'notebooks', capability: 'visible' },
  responseops: { moduleId: 'responseops', capability: 'visible' },
  risk_command_center: { moduleId: 'risk_command_center', capability: 'visible' },
  advanced_hunting: { moduleId: 'advanced_hunting', capability: 'visible' },
  phishing: { moduleId: 'phishing', capability: 'visible' },
  darkweb: { moduleId: 'darkweb', capability: 'visible' },
  spiderfoot: { moduleId: 'spiderfoot', capability: 'visible' },
  dnstwist: { moduleId: 'dnstwist', capability: 'visible' },
  coverage: { moduleId: 'coverage', capability: 'visible' },
  oob_coverage_assessment: { moduleId: 'coverage', capability: 'view_oob_assessment' },
  custom_analytics_studio: { moduleId: 'custom_analytics', capability: 'visible' },
  vulnerability_mgmt: { moduleId: 'vulnerability_mgmt', capability: 'visible' },
  entity_360: { moduleId: 'entity_360', capability: 'visible' },
  soc_digital_twin: { moduleId: 'soc_digital_twin', capability: 'visible' },
  soc_debt: { moduleId: 'soc_debt', capability: 'visible' },
  report_builder: { moduleId: 'report_builder', capability: 'visible' },
  connector_health: { moduleId: 'connector_health', capability: 'visible' },
  detection_quality: { moduleId: 'detection_quality', capability: 'visible' },
  customer_exec: { moduleId: 'customer_exec', capability: 'visible' },
  datalake: { moduleId: 'ai_assistant', capability: 'visible' },
  ioc: { moduleId: 'threat_intel', capability: 'visible' },
  security_copilot: { moduleId: 'security_copilot', capability: 'visible' },
  reports: { moduleId: 'reports', capability: 'visible' },
  masters: { moduleId: 'masters', capability: 'visible' },
  log_master: { moduleId: 'log_master', capability: 'visible' },
  logs: { moduleId: 'logs', capability: 'visible' },
};

// KloudSecure incident Events Evidence / rule-query capability patch.
// build_evidence_query gates who may generate the incident Events Evidence KQL.
// run_evidence_query gates who may execute it.
// set_query gates who may inject/open the generated KQL in the hunting UI.
export const INCIDENT_EVENTS_RULEQUERY_CAPABILITIES = ["build_evidence_query", "run_evidence_query", "set_query", "view_explainability", "view_attack_replay", "use_autopilot", "approve_autopilot", "simulate_response", "view_sla_prediction", "generate_customer_update", "post_customer_update_comment", "export_customer_update_email", "add_customer_update_to_jira", "use_guided_triage", "update_guided_triage", "view_vulnerability_correlation", "find_similar_incidents"] as const;


// KloudSecure Case Evidence Phase 2 capability set.
export const CASE_EVIDENCE_CAPABILITIES = ["read", "upload", "download", "delete", "restore", "view_deleted", "update_metadata", "view_hash", "view_scan_result", "view_audit", "view_storage_metadata", "manage_retention", "lock_evidence", "unlock_evidence", "manage_legal_hold", "mark_customer_visible", "view_customer_artifacts", "create_export_pack", "download_export_pack", "generate_auto_pack", "export"] as const;
